// MfaSettingsCard — mockup screen 5 (password user) + screen 6 (SSO-managed
// read-only variant). Reads LIVE MFA status from GET /users/me (D7): the On/Off
// state is whether a *preferred* (challengeable) method exists — not merely
// "enrolled". The SSO-managed variant renders when `mfa_managed_externally`
// (server: AdminGetUser.UserStatus == EXTERNAL_PROVIDER) is true — NOT on a
// mere `identities`/isFederated signal (D9/R8).
//
// All state-changing calls go through the D14 server endpoints. Opt-out (D8)
// runs a full background re-auth (password + a current code) via the login
// raw-auth path WITHOUT DEVICE_KEY, then hits the server disable endpoint.
// iflow palette only; never a dark theme.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { STORAGE_KEYS } from '../../constants/config'
import {
  Shield, Smartphone, MessageSquare, Mail, Key, Loader2, Check, X, AlertTriangle, Building2, RefreshCw, Download, Copy,
} from 'lucide-react'
import {
  getMyMfaStatus,
  disableMfa,
  setMfaPreference,
  regenerateRecoveryCodes,
  type MfaMethodToken,
  type MfaStatusInfo,
  type RecoveryCodesGenerated,
} from '../../services/api'
import MfaEnrollmentWizard from './MfaEnrollmentWizard'

interface MfaRuntimeConfig { MFA_REMEMBER_DEVICE?: boolean }
function rememberDeviceEnabled(): boolean {
  const cfg = (typeof window !== 'undefined' ? (window.INNOVATEFLOW_CONFIG as unknown as MfaRuntimeConfig) : null)
  return cfg?.MFA_REMEMBER_DEVICE === true
}

const ORDER: MfaMethodToken[] = ['SOFTWARE_TOKEN_MFA', 'SMS_MFA', 'EMAIL_OTP']
const META: Record<MfaMethodToken, { label: string; icon: typeof Smartphone }> = {
  SOFTWARE_TOKEN_MFA: { label: 'Authenticator app', icon: Smartphone },
  SMS_MFA: { label: 'Text message', icon: MessageSquare },
  EMAIL_OTP: { label: 'Email', icon: Mail },
}

function fmtDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function codeKeyFor(method: MfaMethodToken): string {
  return method === 'SMS_MFA' ? 'SMS_MFA_CODE' : method === 'EMAIL_OTP' ? 'EMAIL_OTP_CODE' : 'SOFTWARE_TOKEN_MFA_CODE'
}

/** Read `auth_time` (real sign-in timestamp) from the stored ID token, formatted
 *  in AU locale. Returns '' when unavailable — never fabricated. */
function lastSignInLabel(): string {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
    if (!token) return ''
    const part = token.split('.')[1]
    if (!part) return ''
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '='
    const payload = JSON.parse(atob(b64)) as { auth_time?: number; iat?: number }
    const secs = payload.auth_time ?? payload.iat
    if (!secs) return ''
    return new Date(secs * 1000).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return ''
  }
}

/**
 * D8 opt-out re-auth: prove password + possession WITHOUT a remembered device.
 * InitiateAuth(USER_PASSWORD_AUTH) with NO DEVICE_KEY → answer the returned MFA
 * challenge (via SELECT_MFA_TYPE when Cognito asks) with a fresh code. Returns
 * true only when an AuthenticationResult is produced. Tokens are DISCARDED — the
 * current session is left untouched.
 */
async function reauthWithCode(email: string, password: string, code: string, preferred: MfaMethodToken): Promise<void> {
  const config = window.INNOVATEFLOW_CONFIG
  const url = `https://cognito-idp.${config.REGION}.amazonaws.com/`
  const call = (target: string, body: unknown) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}` },
      body: JSON.stringify(body),
    }).then((r) => r.json())

  let data = await call('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.COGNITO_CLIENT_ID,
    AuthParameters: { USERNAME: email, PASSWORD: password }, // deliberately NO DEVICE_KEY
  })
  if (data.__type || data.message) throw new Error(data.message || 'Password check failed.')

  // If Cognito asks which factor, select the preferred one to get its challenge.
  if (data.ChallengeName === 'SELECT_MFA_TYPE') {
    data = await call('RespondToAuthChallenge', {
      ClientId: config.COGNITO_CLIENT_ID,
      ChallengeName: 'SELECT_MFA_TYPE',
      Session: data.Session,
      ChallengeResponses: { USERNAME: email, ANSWER: preferred },
    })
  }

  if (data.AuthenticationResult) return // already proven (unlikely for an MFA user)

  const challenge = data.ChallengeName as MfaMethodToken | undefined
  if (!challenge || !data.Session) throw new Error('Could not start the verification challenge.')
  const resp = await call('RespondToAuthChallenge', {
    ClientId: config.COGNITO_CLIENT_ID,
    ChallengeName: challenge,
    Session: data.Session,
    ChallengeResponses: { USERNAME: email, [codeKeyFor(challenge)]: code },
  })
  if (!resp.AuthenticationResult) throw new Error(resp.message || 'That code was not accepted.')
  // Success — discard resp.AuthenticationResult; current session stays as-is.
}

function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string; error?: string } } }).response
    const m = resp?.data?.message || resp?.data?.error
    if (m) return m
  }
  return e instanceof Error ? e.message : fallback
}

export default function MfaSettingsCard() {
  const { user } = useAuth()
  const { data, isLoading, refetch } = useQuery({ queryKey: ['mfa-status'], queryFn: getMyMfaStatus })
  const [wizardOpen, setWizardOpen] = useState(false)
  const [optOutOpen, setOptOutOpen] = useState(false)
  const [regenOpen, setRegenOpen] = useState(false)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [actionError, setActionError] = useState('')
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(t)
  }, [toast])

  const mfa: MfaStatusInfo | undefined = data?.mfa
  const managedExternally = data?.mfa_managed_externally === true
  const idpEnforces = data?.idp_enforces_mfa === true

  const orgName =
    (user?.org_memberships?.find((m) => m.org_id === user.active_org_id)?.org_name)
    || user?.org_memberships?.[0]?.org_name
    || 'your organisation'

  // ---- SSO-managed read-only variant (mockup screen 6) --------------------
  if (!isLoading && managedExternally) {
    const signIn = lastSignInLabel()
    return (
      <div className="light-card p-6 mb-6">
        <div className="rounded-xl bg-iflow-50 border border-iflow-200 p-4 mb-5 flex items-start gap-3">
          <Building2 size={22} className="text-iflow-600 shrink-0 mt-0.5" />
          <div className="text-sm text-iflow-900">
            <strong>Your sign-in security is managed by {orgName}.</strong>
            <p className="text-iflow-800/80 text-xs mt-1">
              You sign in through your organisation&apos;s identity provider. Password and multi-factor policies —
              including MFA at sign-in — are set and enforced there, not in InnovateFlow.
            </p>
          </div>
        </div>
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-iflow-500" />
            <h3 className="font-bold text-iflow-900">Two-factor authentication</h3>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700">Managed by your organisation</span>
          </div>
          <MasterToggle on disabled />
        </div>
        <p className="text-sm text-slate-600 mb-3">
          {idpEnforces
            ? <>MFA was verified by {orgName} when you signed in <span className="text-slate-400">(via your organisation&apos;s identity provider)</span>. There is nothing to configure here.</>
            : <>Authentication is handled by {orgName} when you sign in <span className="text-slate-400">(via your organisation&apos;s identity provider)</span>. There is nothing to configure here.</>}
        </p>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shrink-0"><Check size={12} /></span>
          {idpEnforces
            ? <>Last sign-in: MFA satisfied at {orgName}{signIn ? ` · ${signIn}` : ''}</>
            : <>Signed in via {orgName}{signIn ? ` · ${signIn}` : ''}</>}
        </div>
      </div>
    )
  }

  // ---- Password-user variant (mockup screen 5) ----------------------------
  const methods = mfa?.methods ?? []
  const preferred = mfa?.preferred ?? null
  const isOn = !!preferred
  const enabledCount = methods.length
  const recovery = mfa?.recovery

  async function handleMakePreferred(method: MfaMethodToken) {
    setActionError('')
    setBusyAction(`pref-${method}`)
    try {
      await setMfaPreference(method)
      await refetch()
      setToast('Preferred method updated')
    } catch (e) {
      setActionError(errMsg(e, 'Could not update your preferred method.'))
    } finally {
      setBusyAction(null)
    }
  }

  function handleToggle() {
    if (isOn) setOptOutOpen(true)
    else setWizardOpen(true)
  }

  return (
    <div className="light-card p-6 mb-6">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-iflow-500" />
          <h3 className="font-bold text-iflow-900">Two-factor authentication</h3>
          {!isLoading && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isOn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {isOn ? 'On' : 'Off'}
            </span>
          )}
        </div>
        <MasterToggle on={isOn} onClick={handleToggle} disabled={isLoading} />
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Require a second verification step when you sign in with your password. You choose the methods; turn it off any time.
      </p>

      {actionError && (
        <p role="alert" className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 flex items-center gap-2">
          <AlertTriangle size={14} /> {actionError}
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 size={16} className="animate-spin" /> Loading two-factor status…</div>
      ) : (
        <>
          {/* Method list */}
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white/60 mb-4">
            {ORDER.map((m) => {
              const meta = META[m]
              const Icon = meta.icon
              const enrolled = methods.find((x) => x.type === m)
              const isPreferred = preferred === m
              const isLast = enabledCount === 1 && !!enrolled
              return (
                <div key={m} className="flex items-center gap-3 px-4 py-3">
                  <Icon size={20} className={enrolled ? 'text-iflow-600' : 'text-slate-300'} />
                  <div className="flex-1 min-w-0">
                    {enrolled ? (
                      <>
                        <div className="flex items-center gap-2 text-sm font-semibold text-iflow-900">
                          {meta.label}
                          {isPreferred
                            ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700">Preferred</span>
                            : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Backup</span>}
                        </div>
                        <div className="text-xs text-slate-500">{enrolled.added_at ? `Added ${fmtDate(enrolled.added_at)}` : 'Enabled'}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-semibold text-slate-400">{meta.label}</div>
                        <div className="text-xs text-slate-400">Not set up</div>
                      </>
                    )}
                  </div>
                  {enrolled ? (
                    <div className="flex items-center gap-3 shrink-0">
                      {!isPreferred && (
                        <button
                          type="button"
                          onClick={() => handleMakePreferred(m)}
                          disabled={busyAction === `pref-${m}`}
                          className="text-xs text-iflow-600 font-medium hover:text-iflow-800 disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          {busyAction === `pref-${m}` && <Loader2 size={12} className="animate-spin" />} Make preferred
                        </button>
                      )}
                      {isLast ? (
                        <button
                          type="button"
                          onClick={() => setOptOutOpen(true)}
                          className="text-xs text-slate-500 hover:text-rose-600 font-medium"
                          title="This is your only method — removing it turns off two-factor authentication."
                        >
                          Remove
                        </button>
                      ) : (
                        // Single-method removal has no server endpoint in the Phase-2
                        // contract (only disable-all exists). Rather than fake it, the
                        // control is honestly disabled — turn MFA off to clear all
                        // methods, or make another method preferred first.
                        <button
                          type="button"
                          disabled
                          className="text-xs text-slate-300 font-medium cursor-not-allowed"
                          title="To remove a single method, turn off two-factor authentication (removes all methods)."
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setWizardOpen(true)}
                      className="text-xs font-semibold text-iflow-600 border border-iflow-300 rounded-lg px-3 py-1.5 hover:bg-iflow-50 shrink-0"
                    >
                      + Add
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {/* Recovery codes */}
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white/60 px-4 py-3 mb-4">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-iflow-600" />
              <div>
                <div className="text-sm font-semibold text-iflow-900">Recovery codes</div>
                <div className="text-xs text-slate-500">
                  {recovery
                    ? <>{recovery.remaining} of 8 remaining{recovery.generated_at ? ` · generated ${fmtDate(recovery.generated_at)}` : ''}</>
                    : 'Available once two-factor authentication is on'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setRegenOpen(true)}
              disabled={!isOn}
              className="text-xs font-semibold text-iflow-600 border border-iflow-300 rounded-lg px-3 py-1.5 hover:bg-iflow-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Regenerate
            </button>
          </div>

          {/* Trusted devices — Phase 4 (D5), rendered only behind MFA_REMEMBER_DEVICE */}
          {rememberDeviceEnabled() && (
            <div className="rounded-xl border border-slate-200 bg-white/60 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-iflow-900">Trusted devices</div>
              </div>
              <p className="text-xs text-slate-500">No trusted devices are remembered yet. Choose &ldquo;Remember this device&rdquo; at sign-in to add one.</p>
            </div>
          )}

          {!isOn && (
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="mt-2 w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700"
            >
              Set up two-factor authentication
            </button>
          )}
        </>
      )}

      <MfaEnrollmentWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onEnrolled={() => { refetch(); setToast('Two-factor authentication is on') }}
        hasRecoveryCodes={!!recovery && recovery.remaining > 0}
        hasActiveMfa={isOn}
      />

      {optOutOpen && (
        <OptOutDialog
          email={user?.email || ''}
          preferred={preferred ?? 'SOFTWARE_TOKEN_MFA'}
          onClose={() => setOptOutOpen(false)}
          onDone={() => { setOptOutOpen(false); refetch(); setToast('Two-factor authentication turned off') }}
        />
      )}

      {regenOpen && (
        <RegenerateDialog
          onClose={() => setRegenOpen(false)}
          onDone={() => { refetch(); setToast('New recovery codes generated') }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-[80] flex items-center gap-2 rounded-xl bg-iflow-900 text-white px-4 py-3 text-sm shadow-2xl">
          <Check size={16} className="text-emerald-300" /> {toast}
        </div>
      )}
    </div>
  )
}

// ---- Master toggle (rounded-full track + knob) ----------------------------
function MasterToggle({ on, onClick, disabled }: { on: boolean; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Toggle two-factor authentication"
      onClick={onClick}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? 'bg-iflow-500' : 'bg-slate-300'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  )
}

// ---- Opt-out dialog (D8: re-auth password + current code, then disable) ---
function OptOutDialog({ email, preferred, onClose, onDone }: {
  email: string
  preferred: MfaMethodToken
  onClose: () => void
  onDone: () => void
}) {
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function confirm() {
    setError('')
    if (!password) { setError('Enter your current password.'); return }
    if (!code.trim()) { setError('Enter a current code from your preferred method.'); return }
    setBusy(true)
    try {
      await reauthWithCode(email, password, code.trim(), preferred)
      await disableMfa()
      onDone()
    } catch (e) {
      setError(errMsg(e, 'Could not turn off two-factor authentication.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Turn off two-factor authentication">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600"><AlertTriangle size={20} /></span>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-iflow-900">Turn off two-factor authentication?</h2>
            <p className="mt-1 text-sm text-slate-600">Your account will rely on your password alone. Confirm with your current password and a code from your preferred method.</p>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="text-slate-400 hover:text-slate-600" disabled={busy}><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="optout-pw" className="block text-sm font-semibold text-iflow-900 mb-1">Current password</label>
            <input id="optout-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" />
          </div>
          <div>
            <label htmlFor="optout-code" className="block text-sm font-semibold text-iflow-900 mb-1">Verification code</label>
            <input id="optout-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" placeholder="000000" />
          </div>
        </div>
        {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={confirm} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Turn off MFA
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Regenerate recovery codes (shows new codes ONCE) ---------------------
function RegenerateDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<RecoveryCodesGenerated | null>(null)
  const [copied, setCopied] = useState(false)

  async function generate() {
    setError('')
    setBusy(true)
    try {
      const res = await regenerateRecoveryCodes()
      setResult(res)
      onDone()
    } catch (e) {
      setError(errMsg(e, 'Could not regenerate recovery codes.'))
    } finally {
      setBusy(false)
    }
  }

  function download() {
    if (!result) return
    const blob = new Blob([`InnovateFlow recovery codes\n\n${result.codes.join('\n')}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'innovateflow-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function copy() {
    if (!result) return
    try { await navigator.clipboard.writeText(result.codes.join('\n')); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* download remains */ }
  }

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Regenerate recovery codes">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2"><Key size={18} className="text-iflow-600" /><h2 className="text-lg font-semibold text-iflow-900">Recovery codes</h2></div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="text-slate-400 hover:text-slate-600" disabled={busy}><X size={18} /></button>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-slate-600">Regenerating creates a new set of 8 codes and <strong>invalidates your existing codes</strong>. You&apos;ll see the new codes once — save them somewhere safe.</p>
            {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={generate} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-iflow-700 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Regenerate codes
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-3">Each code signs you in once if you lose access to your methods. This is the only time they&apos;re shown.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3">
              {result.codes.map((c) => <span key={c}>{c}</span>)}
            </div>
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={download} className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-iflow-300 text-iflow-700 text-sm font-semibold rounded-lg hover:bg-iflow-50"><Download size={14} /> Download .txt</button>
              <button type="button" onClick={copy} className="flex-1 inline-flex items-center justify-center gap-1 py-2 border border-iflow-300 text-iflow-700 text-sm font-semibold rounded-lg hover:bg-iflow-50">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <button type="button" onClick={onClose} className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 inline-flex items-center justify-center gap-2">
              <RefreshCw size={14} /> Done
            </button>
          </>
        )}
      </div>
    </div>
  )
}
